import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { useSites } from 'hooks/sites/useSites';

import { successToast } from 'utils/successToast';

import { selectors } from 'store/slices/washhub';

import { useMutation, useQueryClient } from 'react-query';
import { acceptOrder, deleteOrders, updateOrder } from 'api/order';

import { Order } from 'types/order';
import { RefType, EditOrderModalProps } from './EditOrderModal.props';

import EditOrderModalView from './EditOrderModal.view';
import { toApiPayloadDateFormat } from 'utils/dateUtils';

const Container = forwardRef<RefType, EditOrderModalProps>((props, ref) => {
  const {
    isCustomerRequest = false,
    renderNextOrderButton,
    renderPreviousOrderButton,
  } = props;

  const queryClient = useQueryClient();
  const currentWashhub = selectors.useSelectedWashHub();

  const [order, setOrder] = useState<Order | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isRequest, setIsRequest] = useState(isCustomerRequest);

  const { isOpen, onOpen, onClose: _onClose } = useDisclosure();

  const orderId = order?._id || '';

  const { data: sites = [] } = useSites();

  const onClose = () => {
    _onClose();

    setIsEditing(false);

    if (isCustomerRequest) {
      setIsRequest(true);
    }
  };

  const onMtnSuccess = (message: string) => () => {
    successToast(message);

    queryClient.invalidateQueries(`${currentWashhub?._id}-fetch-orders`);

    queryClient.invalidateQueries(
      `fetch-orders-status-count-${currentWashhub?._id}`,
    );

    queryClient.invalidateQueries(`fetch-orders`);

    onClose();
  };

  const deleteMtn = useMutation(deleteOrders, {
    onSuccess: onMtnSuccess('Order Deleted'),
  });

  const updateMtn = useMutation(updateOrder, {
    onSuccess: onMtnSuccess('Order Updated Successfully'),
  });

  const acceptMtn = useMutation(
    (payload: any) => acceptOrder(payload, orderId),
    {
      onSuccess: onMtnSuccess('Order Accepted Successfully'),
    },
  );

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_order) => {
        setOrder(_order);

        onOpen();
      },
    }),
    [onOpen],
  );

  const onSubmit = (values: any) => {
    const site = values?.site;
    const _site: any = sites?.find((s) => s._id === site);

    const siteCode = _site?.code || '';
    const dayOfWeek = Number(_site?.dayOfWeek || '0');
    const washHub = currentWashhub?._id || '';
    const washHubCode = currentWashhub?.code || '';
    const deliveryAt = toApiPayloadDateFormat(values?.deliveryAt);

    const details = values?.details?.map((d: any) => {
      return {
        ...d,
        casesCount: Number(d?.casesCount || '0'),
        totalItems: Number(d?.totalItems || '0'),
      };
    });

    const payload = {
      _id: order?._id,
      site,
      details,
      washHub,
      siteCode,
      dayOfWeek,
      deliveryAt,
      washHubCode,
      notes: Boolean(values?.notes) ? values?.notes : '',
    };

    if (isCustomerRequest) {
      acceptMtn.mutate(payload);

      return;
    }

    updateMtn.mutate(payload);
  };

  const _props = {
    order,
    isOpen,
    onClose,
    onSubmit,
    isEditing,
    setIsEditing,
    isRequest,
    setIsRequest,
    isPendingInvoiceOrder: typeof order?.invoiced === 'boolean',
    isLoading: updateMtn.isLoading || acceptMtn.isLoading,
    isDeleting: deleteMtn.isLoading,
    isCustomerRequest,
    renderNextOrderButton,
    renderPreviousOrderButton,
    onDelete: () => {
      if (!order?._id) {
        return;
      }

      deleteMtn.mutate([order._id]);
    },
  };

  return <EditOrderModalView {..._props} />;
});

const EditOrderModal = memo(Container);

export default EditOrderModal;
